import { useState } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { useRoute } from "@/app/queries";
import { useMe } from "@/app/session";
import { EmptyState, SkeletonRows } from "@/ui/states";
import { StepMark } from "@/ui/StepMark";
import { STATE_MARK } from "@/ui/route";
import { NetworkError } from "./route/bits";
import { defaultStepId, findStep, mainTracks, parallelTracks } from "./route/chain";
import { MapAside } from "./route/MapAside";
import { ParallelLane, TrackColumns } from "./route/TrackColumns";

/** S2 «Карта маршрута», вариант 2a: колонки треков слева направо,
 *  «Алгоритмы» пунктиром снизу, справа панель выбранного шага. */
export function RouteMapPage() {
  const route = useRoute();
  const me = useMe();
  const [picked, setPicked] = useState<string>();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;

  if (route.status === "pending") {
    return (
      <Shell nav wide user={user}>
        <div className="px-6 py-[22px]">
          <SkeletonRows rows={6} />
        </div>
      </Shell>
    );
  }
  if (route.status === "error") {
    return (
      <Shell nav wide user={user}>
        <div className="mx-auto max-w-[720px] px-6 py-[22px]">
          <NetworkError retry={() => void route.refetch()} />
        </div>
      </Shell>
    );
  }
  const r = route.data;
  if (r.tracks.length === 0) {
    return (
      <Shell nav wide user={user}>
        <div className="mx-auto max-w-[720px] px-6 py-[22px]">
          <EmptyState where="студент · карта треков" title="Маршрут ещё не собран" body="Треки и шаги появятся, когда администратор опубликует курс. Ваш прогресс ничего не теряет — карта заполнится сама." primary={{ label: "на мой маршрут", href: "/" }} />
        </div>
        <TabBar />
      </Shell>
    );
  }
  const main = mainTracks(r);
  const parallel = parallelTracks(r);
  const selectedId = picked ?? defaultStepId(r);
  const selected = selectedId ? findStep(r, selectedId) : undefined;

  return (
    <Shell nav wide user={user}>
      <div className="grid pb-24 md:grid-cols-[minmax(0,1fr)_340px] md:pb-0">
        <div className="px-4 pb-[26px] pt-[22px] md:px-6">
          <header className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
            <div>
              <h1 className="m-0 font-mono text-[22px] font-bold leading-[1.15] tracking-[-.02em] text-ink">Основная последовательность</h1>
              <p className="m-0 mt-1 text-[12.5px] text-ink2">шаг открывается, когда сдан гейт предыдущего</p>
              <p className="m-0 mt-1 font-mono text-[11.5px] text-accdim" data-testid="route-total">
                {r.done} / {r.total} шагов сдано
              </p>
            </div>
            <ul aria-label="легенда" className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1.5 p-0">
              {(["done", "active", "open", "locked"] as const).map((s) => (
                <li key={s} className="flex items-center gap-1.5 font-mono text-[11px] text-ink2">
                  <StepMark state={s} size={16} />
                  {STATE_MARK[s].label}
                </li>
              ))}
            </ul>
          </header>
          <TrackColumns tracks={main} selectedId={selectedId} onSelect={setPicked} />
          {parallel.map((t) => (
            <ParallelLane key={t.id} track={t} selectedId={selectedId} onSelect={setPicked} />
          ))}
        </div>
        {selected && <MapAside track={selected.track} step={selected.step} />}
      </div>
      <TabBar />
    </Shell>
  );
}
